import { TFunction } from 'i18next';
import {
  PasskeyAttestationWithMeta,
  PasskeyLoginChallengeResponse,
  PasskeyLoginVerifyRequest,
  PasskeyRegisterChallengeResponse,
} from '~/types/passkey';

class PasskeyNotSupportedError extends Error {
  constructor() {
    super('Passkeys are not supported in this browser');
    this.name = 'PasskeyNotSupportedError';
  }
}

const loadSimpleWebAuthnBrowser = async () => {
  return await import('@simplewebauthn/browser');
};

export const isPasskeySupported = () => {
  return typeof window !== 'undefined' && 'PublicKeyCredential' in window;
};

const assertPasskeySupport = () => {
  if (!isPasskeySupported()) {
    throw new PasskeyNotSupportedError();
  }
};

export const createPasskeyAttestation = async (
  options: PasskeyRegisterChallengeResponse,
): Promise<PasskeyAttestationWithMeta> => {
  assertPasskeySupport();

  const { startRegistration } = await loadSimpleWebAuthnBrowser();

  const registration = await startRegistration({
    optionsJSON: options,
  });

  return {
    attestation_object: registration.response.attestationObject,
    client_data_json: registration.response.clientDataJSON,
    authenticatorAttachment: registration.authenticatorAttachment ?? null,
  };
};

export const generatePasskeyLabel = (
  authenticatorAttachment: string | null,
  firstName: string,
  t: TFunction<'settings', 'passkey.device'>,
): string => {
  if (authenticatorAttachment !== 'platform') {
    return t('security_key');
  }

  const ua = navigator.userAgent;
  let device: 'iphone' | 'ipad' | 'mac' | 'windows' | 'android' | 'generic';

  if (ua.includes('iPhone')) {
    device = 'iphone';
  } else if (ua.includes('iPad')) {
    device = 'ipad';
  } else if (/Macintosh|MacIntel/.test(ua)) {
    device = 'mac';
  } else if (ua.includes('Windows')) {
    device = 'windows';
  } else if (ua.includes('Android')) {
    device = 'android';
  } else {
    device = 'generic';
  }

  return t('label', { device: t(device), firstName });
};

export const createPasskeyAuthentication = async (
  options: PasskeyLoginChallengeResponse,
): Promise<PasskeyLoginVerifyRequest> => {
  assertPasskeySupport();

  const { startAuthentication } = await loadSimpleWebAuthnBrowser();

  const authentication = await startAuthentication({
    optionsJSON: options,
  });

  return {
    id: authentication.id,
    authenticator_data: authentication.response.authenticatorData,
    client_data_json: authentication.response.clientDataJSON,
    signature: authentication.response.signature,
    user_handle: authentication.response.userHandle ?? null,
  };
};

export const isPasskeyNotSupportedError = (error: unknown): error is PasskeyNotSupportedError => {
  return error instanceof PasskeyNotSupportedError;
};

export const isPasskeyCancelledError = (error: unknown): boolean => {
  return error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'AbortError');
};

const HAS_PASSKEY_KEY = 'sibill_has_passkey';
const ACTIVE_PASSKEY_KEY = 'sibill_active_passkey_id';

const getPasskeyCount = (): number => {
  return parseInt(localStorage.getItem(HAS_PASSKEY_KEY) ?? '0', 10);
};

export const incrementPasskeyCount = () => {
  localStorage.setItem(HAS_PASSKEY_KEY, String(getPasskeyCount() + 1));
};

export const decrementPasskeyCount = () => {
  const count = getPasskeyCount();
  if (count > 0) {
    localStorage.setItem(HAS_PASSKEY_KEY, String(count - 1));
  }
};

export const hasRegisteredPasskey = (): boolean => {
  return getPasskeyCount() > 0;
};

export const setActivePasskeyId = (id: string) => {
  localStorage.setItem(ACTIVE_PASSKEY_KEY, id);
};

export const getActivePasskeyId = (): string | null => {
  return localStorage.getItem(ACTIVE_PASSKEY_KEY);
};

export const clearActivePasskeyId = () => {
  localStorage.removeItem(ACTIVE_PASSKEY_KEY);
};

const PASSKEY_ANNOUNCEMENT_KEY = 'sibill_passkey_announcement_v1';

export const markPasskeyAnnouncementDismissed = () => {
  localStorage.setItem(PASSKEY_ANNOUNCEMENT_KEY, 'true');
};

export const isPasskeyAnnouncementDismissed = (): boolean => {
  return localStorage.getItem(PASSKEY_ANNOUNCEMENT_KEY) === 'true';
};
